import React from "react";
import { motion } from "framer-motion";
import { UserCheck, Zap, Settings, Shield } from "lucide-react";
import SectionHeading from "@/components/shared/SectionHeading";

const engagements = [
  {
    icon: <UserCheck size={24} />,
    title: "Accompagnement unique",
    desc: "Un suivi personnalisé pour chacun de vos projets, de l'enlèvement à la livraison finale.",
    color: "text-[#E8902A]",
    bg: "bg-[#E8902A]/10",
  },
  {
    icon: <Zap size={24} />,
    title: "Réactivité",
    desc: "Une équipe disponible et réactive à toutes vos demandes.",
    color: "text-blue-400",
    bg: "bg-blue-400/10",
  },
  {
    icon: <Settings size={24} />,
    title: "Solutions adaptées",
    desc: "Des prestations techniques sur mesure, adaptées à vos contraintes et à votre budget.",
    color: "text-emerald-400",
    bg: "bg-emerald-400/10",
  },
  {
    icon: <Shield size={24} />,
    title: "Service fiable",
    desc: "Un savoir-faire reconnu, un réseau de transporteurs nationaux et européens qualifiés.",
    color: "text-violet-400",
    bg: "bg-violet-400/10",
  },
];

const IMAGE_FADE_IN = {
  initial: { opacity: 0, scale: 0.96 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true },
  transition: { duration: 0.6, delay: 0.2 },
};

const ENGAGEMENT_ANIMS = engagements.map((_, i) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, delay: i * 0.08 },
}));

export default function WhyUsSection() {
  return (
    <section className="py-24 bg-[#1e3a52] relative overflow-hidden" data-testid="why-us-section">
      <div className="absolute top-20 right-0 w-96 h-96 bg-[#E8902A]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-stretch">

          {/* Colonne gauche — Pourquoi nous choisir */}
          <div>
            <SectionHeading
              label="Pourquoi nous choisir"
              title={<>Votre <span className="gradient-text">partenaire</span><br />logistique de<br /><span className="gradient-text">confiance</span></>}
              description="Plus qu'un prestataire, nous sommes votre copilote logistique. Nous prenons en charge l'intégralité de votre chaîne transport."
            />
            <motion.div
              {...IMAGE_FADE_IN}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="/images/camion-blanc.png"
                  alt="Transport routier Scania"
                  className="w-full h-auto object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a52]/70 via-[#1e3a52]/40 to-[#1e3a52]/80" />
              </div>
              <div className="absolute -bottom-5 left-5 glass-card rounded-xl px-5 py-3">
                <div className="font-heading font-black text-3xl gradient-text">2021</div>
                <div className="text-slate-300 text-xs font-heading uppercase tracking-widest">Fondé à Vichy</div>
              </div>
            </motion.div>
          </div>

          {/* Colonne droite — Nos engagements */}
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-[#E8902A]" />
              <span className="font-heading font-bold text-xs tracking-widest uppercase text-[#E8902A]">
                Nos engagements
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 flex-1">
              {engagements.map((eng, i) => (
                <motion.div
                  key={eng.title}
                  {...ENGAGEMENT_ANIMS[i]}
                  className="group p-5 rounded-xl border border-white/5 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/10 transition-all duration-300 flex flex-col"
                  data-testid={`engagement-card-${i}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${eng.bg} ${eng.color} flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110`}>
                    {eng.icon}
                  </div>
                  <h4 className={`font-heading font-bold text-sm mb-2 ${eng.color}`}>{eng.title}</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">{eng.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
